import React, { useState, useEffect, useMemo, useCallback } from 'react';
import { Link } from 'react-router-dom';
import ContentHeader from '../components/ContentHeader';
import InfoIconButton from '../components/InfoIconButton';
import { useTenantFromUrl } from '../hooks/useTenantFromUrl';
import { mailingListBuilderAPI } from '../services/api';
import { hasPermission } from '../utils/permissions';
import { formatDisplayDate } from '../utils/dateFormat';
import '../styles/FormGrid.css';
import './MailingListBuilder.css';

function todayIsoDate() {
  const d = new Date();
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, '0');
  const day = String(d.getDate()).padStart(2, '0');
  return `${y}-${m}-${day}`;
}

function formatClientName(row) {
  const last = row.client_last_name || '';
  const first = row.client_first_name || '';
  const n = [last, first].filter(Boolean).join(', ');
  return n || '—';
}

function formatContactName(row) {
  const last = row.contact_last_name || '';
  const first = row.contact_first_name || '';
  return [last, first].filter(Boolean).join(', ') || '—';
}

function selectedUuidsFromMultiselect(el) {
  if (!el) return [];
  return Array.from(el.selectedOptions)
    .map((o) => o.value)
    .filter((v) => v && v.length > 0);
}

function MailingListBuilder() {
  const { tenantPath } = useTenantFromUrl();
  const canUse = hasPermission('mailing_list_builder.use');

  const [optionsLoading, setOptionsLoading] = useState(true);
  const [optionsError, setOptionsError] = useState(null);
  const [cities, setCities] = useState([]);
  const [schoolDistricts, setSchoolDistricts] = useState([]);
  const [diagnoses, setDiagnoses] = useState([]);

  const [dobFrom, setDobFrom] = useState('');
  const [dobTo, setDobTo] = useState('');
  const [ageMin, setAgeMin] = useState('');
  const [ageMax, setAgeMax] = useState('');
  const [asOfDate, setAsOfDate] = useState(todayIsoDate);
  const [diagnosisId, setDiagnosisId] = useState('');

  const citiesSelectRef = React.useRef(null);
  const schoolsSelectRef = React.useRef(null);

  const [requireEmail, setRequireEmail] = useState(true);
  const [searching, setSearching] = useState(false);
  const [searchError, setSearchError] = useState(null);
  const [rows, setRows] = useState([]);
  const [truncated, setTruncated] = useState(false);
  const [copyHint, setCopyHint] = useState(null);
  const [hasSearched, setHasSearched] = useState(false);
  /** Page description next to title; ⓘ toggles visibility (default closed, same as other info icons) */
  const [pageDescOpen, setPageDescOpen] = useState(false);

  useEffect(() => {
    if (!canUse) {
      setOptionsLoading(false);
      return;
    }
    let cancelled = false;
    (async () => {
      try {
        setOptionsError(null);
        const res = await mailingListBuilderAPI.getOptions();
        if (cancelled) return;
        setCities(res.data?.cities || []);
        setSchoolDistricts(res.data?.school_districts || []);
        setDiagnoses(res.data?.diagnoses || []);
      } catch (e) {
        if (!cancelled) setOptionsError(e.response?.data?.error || e.message || 'Failed to load filter options');
      } finally {
        if (!cancelled) setOptionsLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [canUse]);

  const bccLine = useMemo(() => {
    const seen = new Set();
    const emails = [];
    for (const r of rows) {
      const em = (r.contact_email || '').trim().toLowerCase();
      if (em && !seen.has(em)) {
        seen.add(em);
        emails.push(r.contact_email.trim());
      }
    }
    return emails.join('; ');
  }, [rows]);

  const clearFilters = useCallback(() => {
    setDobFrom('');
    setDobTo('');
    setAgeMin('');
    setAgeMax('');
    setAsOfDate(todayIsoDate());
    setDiagnosisId('');
    setRequireEmail(true);
    if (citiesSelectRef.current) {
      Array.from(citiesSelectRef.current.options).forEach((o) => {
        o.selected = false;
      });
    }
    if (schoolsSelectRef.current) {
      Array.from(schoolsSelectRef.current.options).forEach((o) => {
        o.selected = false;
      });
    }
  }, []);

  const runSearch = useCallback(
    async (e) => {
      e?.preventDefault?.();
      if (!canUse) return;
      const city_ids = selectedUuidsFromMultiselect(citiesSelectRef.current);
      const school_ids = selectedUuidsFromMultiselect(schoolsSelectRef.current);
      const body = {
        city_ids,
        school_ids,
        diagnosis_id: diagnosisId || null,
        dob_from: dobFrom || null,
        dob_to: dobTo || null,
        age_min: ageMin === '' ? null : Number(ageMin),
        age_max: ageMax === '' ? null : Number(ageMax),
        as_of_date: asOfDate || null,
        require_email: requireEmail,
      };
      setSearching(true);
      setSearchError(null);
      setCopyHint(null);
      try {
        const res = await mailingListBuilderAPI.search(body);
        setRows(res.data?.rows || []);
        setTruncated(!!res.data?.truncated);
        setHasSearched(true);
      } catch (err) {
        setRows([]);
        setTruncated(false);
        setSearchError(err.response?.data?.error || err.message || 'Search failed');
        setHasSearched(true);
      } finally {
        setSearching(false);
      }
    },
    [canUse, diagnosisId, dobFrom, dobTo, ageMin, ageMax, asOfDate, requireEmail]
  );

  const downloadCsv = useCallback(() => {
    if (!rows.length) return;
    const headers = [
      'Client Last', 'Client First', 'Date of Birth',
      'Contact Last', 'Contact First', 'Phone', 'Email',
      'Street 1', 'Street 2', 'City', 'State', 'ZIP', 'County',
      'District', 'Diagnosis',
    ];
    const escape = (v) => {
      const s = v == null ? '' : String(v);
      return s.includes(',') || s.includes('"') || s.includes('\n') ? `"${s.replace(/"/g, '""')}"` : s;
    };
    const lines = [
      headers.join(','),
      ...rows.map((r) => [
        r.client_last_name, r.client_first_name,
        r.date_of_birth ? formatDisplayDate(r.date_of_birth) : '',
        r.contact_last_name, r.contact_first_name,
        r.contact_phone, r.contact_email,
        r.street_1, r.street_2, r.city_label, r.state, r.zip, r.county_label,
        r.district_code, r.diagnosis_display,
      ].map(escape).join(',')),
    ];
    const blob = new Blob([lines.join('\r\n')], { type: 'text/csv' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `mailing-list-${new Date().toISOString().slice(0, 10)}.csv`;
    a.click();
    URL.revokeObjectURL(url);
  }, [rows]);

  const copyBcc = useCallback(async () => {
    if (!bccLine) {
      setCopyHint('Nothing to copy yet — run a search with matching clients.');
      return;
    }
    try {
      await navigator.clipboard.writeText(bccLine);
      setCopyHint('Copied to clipboard.');
    } catch {
      setCopyHint('Could not copy automatically — select the text and copy manually.');
    }
  }, [bccLine]);

  if (!canUse) {
    return (
      <div className="mailing-list-builder-page">
        <ContentHeader title="Mailing List Builder" />
        <p className="mailing-list-builder-denied">
          You don&apos;t have permission to use the Mailing List Builder. Ask a tenant administrator to grant{' '}
          <strong>Mailing List Builder</strong> in Settings → Roles &amp; Permissions (often given to administrators,
          managers, or a parent liaison role).
        </p>
      </div>
    );
  }

  return (
    <div className="mailing-list-builder-page">
      <ContentHeader
        title={
          <span className="mailing-list-builder-title-cluster" role="group" aria-labelledby="mlb-title-heading">
            <span className="mailing-list-builder-title-lead">
              <span id="mlb-title-heading" className="mailing-list-builder-title-heading">
                Mailing List Builder
              </span>
              <InfoIconButton
                className="mailing-list-builder-title-info-btn"
                ariaExpanded={pageDescOpen}
                ariaControls="mlb-page-desc"
                ariaLabel="Show page description"
                ariaLabelExpanded="Hide page description"
                onClick={() => setPageDescOpen((v) => !v)}
              />
            </span>
            <span
              className="mailing-list-builder-title-desc"
              id="mlb-page-desc"
              role="region"
              hidden={!pageDescOpen}
            >
              Build a list of <strong>primary contact</strong> information for <strong>active clients</strong>{' '}
              (their latest referral is open). Use the filters below, then run a search. For <strong>email blasts</strong>:
              copy the BCC line. For <strong>paper mail</strong>: uncheck &ldquo;Require email&rdquo; and download the CSV —
              addresses come from the primary contact record. City filters use your tenant&apos;s city lookup; clients
              with legacy free-text city are included when the text matches a selected city name.
            </span>
          </span>
        }
      />

      {optionsLoading && <p>Loading filter options…</p>}
      {optionsError && <p className="mailing-list-builder-err">{optionsError}</p>}

      {!optionsLoading && !optionsError && (
        <form className="mailing-list-builder-form" onSubmit={runSearch}>
          <div className="list-edit-form-grid mailing-list-builder-grid">
            <label className="mailing-list-builder-span2">
              Cities (hold Ctrl or ⌘ to select multiple)
              <select ref={citiesSelectRef} className="mailing-list-builder-multiselect" multiple size={8}>
                {cities.map((c) => (
                  <option key={c.id} value={c.id}>
                    {c.city}
                  </option>
                ))}
              </select>
            </label>
            <label className="mailing-list-builder-span2">
              School districts
              <select ref={schoolsSelectRef} className="mailing-list-builder-multiselect" multiple size={8}>
                {schoolDistricts.map((s) => (
                  <option key={s.id} value={s.id}>
                    {s.label}
                  </option>
                ))}
              </select>
            </label>
            <label>
              Diagnosis (Dx1, Dx2, or Dx3)
              <select value={diagnosisId} onChange={(e) => setDiagnosisId(e.target.value)}>
                <option value="">Any</option>
                {diagnoses.map((d) => (
                  <option key={d.id} value={d.id}>
                    {d.description}
                  </option>
                ))}
              </select>
            </label>
            <label>
              Date of birth from
              <input type="date" value={dobFrom} onChange={(e) => setDobFrom(e.target.value)} />
            </label>
            <label>
              Date of birth to
              <input type="date" value={dobTo} onChange={(e) => setDobTo(e.target.value)} />
            </label>
            <div className="mailing-list-builder-age-row" role="group" aria-label="Age filters">
              <label>
                Minimum age (whole years)
                <input
                  type="number"
                  min={0}
                  max={120}
                  value={ageMin}
                  onChange={(e) => setAgeMin(e.target.value)}
                  placeholder="e.g. 2"
                />
              </label>
              <label>
                Maximum age (whole years)
                <input
                  type="number"
                  min={0}
                  max={120}
                  value={ageMax}
                  onChange={(e) => setAgeMax(e.target.value)}
                  placeholder="e.g. 3"
                />
              </label>
              <label>
                As of (for age filters)
                <input type="date" value={asOfDate} onChange={(e) => setAsOfDate(e.target.value)} />
              </label>
            </div>
          </div>
          <p className="mailing-list-builder-hint">
            Use either <strong>date of birth range</strong> and/or <strong>age min/max</strong> with an <strong>As of</strong>{' '}
            date (for example, set As of to a playdate next month so &quot;ages 2–3&quot; means 2–3 on that day).
          </p>
          <label className="mailing-list-builder-checkbox-label">
            <input
              type="checkbox"
              checked={requireEmail}
              onChange={(e) => setRequireEmail(e.target.checked)}
            />
            {' '}Require email address (uncheck to include contacts without email, for paper mail)
          </label>
          <div className="list-edit-form-actions mailing-list-builder-actions">
            <button type="button" className="mailing-list-builder-clear-btn" onClick={clearFilters}>
              Clear filters
            </button>
            <button type="submit" className="btn-primary" disabled={searching}>
              {searching ? 'Searching…' : 'Run search'}
            </button>
          </div>
        </form>
      )}

      {searchError && <p className="mailing-list-builder-err">{searchError}</p>}

      {rows.length > 0 && (
        <section className="mailing-list-builder-results">
          <h2 className="mailing-list-builder-results-title">Results ({rows.length}{truncated ? '+' : ''})</h2>
          {truncated && (
            <p className="mailing-list-builder-truncated">
              Showing the first {rows.length} matches. Narrow your filters if you need a smaller set.
            </p>
          )}
          <label className="mailing-list-builder-bcc-label" htmlFor="mailing-list-bcc">
            BCC / semicolon-separated emails
          </label>
          <textarea
            id="mailing-list-bcc"
            className="mailing-list-builder-bcc"
            readOnly
            rows={3}
            value={bccLine}
          />
          <div className="mailing-list-builder-copy-row">
            <button type="button" className="btn-primary" onClick={copyBcc} disabled={!bccLine}>
              Copy BCC line
            </button>
            <button type="button" className="mailing-list-builder-csv-btn" onClick={downloadCsv}>
              Download CSV
            </button>
            {copyHint && <span className="mailing-list-builder-copy-hint">{copyHint}</span>}
          </div>
          <div className="mailing-list-builder-table-wrap">
            <table className="mailing-list-builder-table">
              <thead>
                <tr>
                  <th>Client</th>
                  <th>DOB</th>
                  <th>Primary contact</th>
                  <th>Phone</th>
                  <th>Email</th>
                  <th>Address</th>
                  <th>District</th>
                  <th>Diagnosis</th>
                </tr>
              </thead>
              <tbody>
                {rows.map((r) => {
                  const addrLine1 = [r.street_1, r.street_2].filter(Boolean).join(', ');
                  const addrLine2 = [r.city_label, r.state, r.zip].filter(Boolean).join(', ');
                  const countyLine = r.county_label ? `${r.county_label} County` : null;
                  return (
                    <tr key={`${r.client_id}-${r.contact_id}`}>
                      <td>
                        <Link to={tenantPath(`/client/${r.client_id}`)}>{formatClientName(r)}</Link>
                      </td>
                      <td>{r.date_of_birth ? formatDisplayDate(r.date_of_birth) : '—'}</td>
                      <td>{formatContactName(r)}</td>
                      <td>{r.contact_phone || '—'}</td>
                      <td>
                        {r.contact_email
                          ? <a href={`mailto:${encodeURIComponent(r.contact_email)}`}>{r.contact_email}</a>
                          : '—'}
                      </td>
                      <td className="mailing-list-builder-table-address">
                        {addrLine1 && <div>{addrLine1}</div>}
                        {addrLine2 && <div>{addrLine2}</div>}
                        {countyLine && <div className="mailing-list-builder-county">{countyLine}</div>}
                        {!addrLine1 && !addrLine2 && '—'}
                      </td>
                      <td>{r.district_code || '—'}</td>
                      <td className="mailing-list-builder-table-diagnosis">{r.diagnosis_display || '—'}</td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </section>
      )}

      {hasSearched && !searching && !searchError && rows.length === 0 && (
        <p className="mailing-list-builder-empty muted">No matching clients with a primary contact email.</p>
      )}
    </div>
  );
}

export default MailingListBuilder;
